import React from 'react'
import { useAccountTypes } from '../AccountTypeContext'
import { useCustomizeDashboard } from '../CustomizeDashboard/CustomizeDashboardContext/CustomizeDashboardContext'
import PartialOutageNotice from '../Shared/partialOutageNotice'
import StaleDataNotice from '../Shared/StaleDataNotice'
import { customErrorAnalytics } from '../analytics'
import WealthProspect from './WealthManagement/WealthProspect'
import WealthProspectOutage from './WealthManagement/WealthProspectOutage'
import { investAccountComponents } from '../CustomizeDashboard/constants'
import { CustomizeDataObj } from '../CustomizeDashboard/types'
import LobShell from '../Shared/LobShell'

interface InvestContentProps {
  hasStaleInvestAccounts: boolean
  isInvestUser: boolean
  isWealthFullOutage: boolean
  showInvestAccounts: boolean
  showOutage: boolean
  showWealthProspect: boolean
  sortedInvestAccountCards: React.ReactElement[] | null
}

const InvestContent: React.FC<InvestContentProps> = ({
  hasStaleInvestAccounts,
  isInvestUser,
  isWealthFullOutage,
  showInvestAccounts,
  showOutage,
  showWealthProspect,
  sortedInvestAccountCards,
}) => {
  const isWealthProspectWithOutage = showWealthProspect && isWealthFullOutage

  if (!isInvestUser && isWealthProspectWithOutage) {
    return <WealthProspectOutage />
  }

  if (showOutage) {
    return <PartialOutageNotice lob="invest" />
  }

  return (
    <>
      {showInvestAccounts && hasStaleInvestAccounts && (
        <StaleDataNotice dataTestId="stale-invest-notice" />
      )}
      {showWealthProspect && <WealthProspect />}
      {showInvestAccounts && sortedInvestAccountCards}
    </>
  )
}

function getSortedAccountTypeComponents(
  customizeData: CustomizeDataObj,
  type: string[],
): React.ReactElement[] {
  return customizeData.accountTypes
    .filter(account => type.includes(account.lob))
    .sort((a, b) => a.order - b.order)
    .map(account => investAccountComponents[account.name])
}

const InvestmentAccounts: React.FC = () => {
  const {
    isInvestUser,
    isInvestOutage,
    hasInvestAccounts,
    hasStaleInvestAccounts,
    isWealthProspect,
    isWealthFullOutage,
  } = useAccountTypes()

  const [{ customizeApiData }] = useCustomizeDashboard()

  const sortedInvestAccountCards = customizeApiData
    ? getSortedAccountTypeComponents(customizeApiData, ['INVEST', 'WEALTH'])
    : null

  React.useEffect((): void => {
    if (isInvestOutage) {
      customErrorAnalytics('Invest Outage')
    }
  }, [isInvestOutage])

  const showInvestAccounts = hasInvestAccounts
  const showOutage = isInvestOutage || isWealthFullOutage

  return (
    <>
      {showOutage || showInvestAccounts || isWealthProspect ? (
        <LobShell header="Investment Accounts" customizeTriggerLob="invest">
          <InvestContent
            hasStaleInvestAccounts={hasStaleInvestAccounts}
            isInvestUser={isInvestUser}
            isWealthFullOutage={isWealthFullOutage}
            showInvestAccounts={showInvestAccounts}
            showOutage={showOutage}
            showWealthProspect={isWealthProspect}
            sortedInvestAccountCards={sortedInvestAccountCards}
          />
        </LobShell>
      ) : null}
    </>
  )
}

export default InvestmentAccounts
